// Product viewer for the Lo-Fi Ambient hat PDP. Four modes:
//   photo     — real product photos in an autoplaying Swiper (default)
//   solid / wireframe / points — a three.js render of the scanned hat
//
// three.js (~600KB) + the GLB load LAZILY — only when a 3D mode is first selected — so a
// photo-default page stays light. Swiper is already in the theme bundle, so it's a static import.
//
// IMPORTANT: three.js / Swiper instances must NOT live in Alpine's reactive data (its Proxy
// breaks three's read-only matrices). They live in module-scoped WeakMaps keyed by the root
// element; only primitive UI state (mode / ready / failed) stays on the reactive component.
//
// Wiring (see sections/main-product-3dhat.liquid):
//   x-data="hatViewer('photo')"     — arg = default mode
//   data-glb-url, data-draco-path, data-cam-dir
//   x-ref="canvas" — 3D mount ;  x-ref="swiper" — the .swiper element for photo mode

import Swiper from 'swiper'
import { Autoplay } from 'swiper/modules'

const scenes = new WeakMap() // el -> three.js ctx
const swipers = new WeakMap() // el -> Swiper instance

export default {
  name: 'hatViewer',
  component() {
    return {
      mode: 'photo', // photo | solid | wireframe | points (overridden by data-default-mode)
      ready: false, // 3D first frame drawn — hides the poster
      failed: false, // WebGL unavailable / 3D load error
      _booted: false,
      _booting: false,

      init() {
        this.mode = this.$root.dataset.defaultMode || 'photo'
        this._initSwiper()
        if (this.mode !== 'photo') this._enter3D(this.mode) // 3D default: boot now
      },

      _initSwiper() {
        const elc = this.$refs.swiper
        if (!elc) return
        const sw = new Swiper(elc, {
          modules: [Autoplay],
          slidesPerView: 1,
          loop: elc.querySelectorAll('.swiper-slide').length > 1,
          autoplay: { delay: 3500, disableOnInteraction: false },
        })
        if (this.mode !== 'photo') sw.autoplay.stop()
        swipers.set(this.$root, sw)
      },

      setMode(m) {
        this.mode = m
        const sw = swipers.get(this.$root)
        if (m === 'photo') {
          if (sw) { sw.update(); sw.autoplay.start() }
          return
        }
        if (sw) sw.autoplay.stop()
        this._enter3D(m)
      },

      // Lazily boot three.js on the first 3D-mode selection; afterwards just toggle reps.
      _enter3D(m) {
        if (!this._supportsWebGL()) { this.failed = true; return }
        if (this._booted) { this._applyThreeMode(m); return }
        if (this._booting) return
        this._booting = true
        this._boot()
          .then(() => { this._booted = true; this._booting = false })
          .catch((err) => {
            console.error('[hatViewer] 3D init failed', err)
            this.failed = true
            this._booting = false
          })
      },

      _supportsWebGL() {
        try {
          const c = document.createElement('canvas')
          return !!(window.WebGLRenderingContext &&
            (c.getContext('webgl') || c.getContext('experimental-webgl')))
        } catch (e) {
          return false
        }
      },

      async _boot() {
        const THREE = await import('three')
        const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader.js')
        const { DRACOLoader } = await import('three/examples/jsm/loaders/DRACOLoader.js')
        const { OrbitControls } = await import('three/examples/jsm/controls/OrbitControls.js')

        const el = this.$root
        const holder = this.$refs.canvas
        const glbUrl = el.dataset.glbUrl
        const dracoPath = el.dataset.dracoPath || 'https://www.gstatic.com/draco/v1/decoders/'

        const scene = new THREE.Scene()
        scene.background = new THREE.Color(0xffffff)
        const camera = new THREE.PerspectiveCamera(45, 1, 0.01, 1000)
        const renderer = new THREE.WebGLRenderer({ antialias: true })
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
        holder.appendChild(renderer.domElement)

        scene.add(new THREE.HemisphereLight(0xffffff, 0x999999, 1.1))
        const key = new THREE.DirectionalLight(0xffffff, 1.4)
        key.position.set(1, 2, 2)
        scene.add(key)

        const controls = new OrbitControls(camera, renderer.domElement)
        controls.enableDamping = true
        controls.dampingFactor = 0.08
        // No auto-rotate: the hat loads logo-forward and STAYS there; users drag to explore.
        // (Auto-spin rotated the logo away regardless of the start angle.)

        const ctx = { THREE, scene, camera, renderer, controls, holder,
          solid: [], wire: [], points: [], raf: 0 }
        scenes.set(el, ctx)

        const draco = new DRACOLoader()
        draco.setDecoderPath(dracoPath)
        const loader = new GLTFLoader()
        loader.setDRACOLoader(draco)

        const gltf = await loader.loadAsync(glbUrl)
        this._addRepresentations(ctx, gltf.scene)
        this._frameModel(ctx)
        this._applyThreeMode(this.mode === 'photo' ? 'solid' : this.mode)

        this._resize()
        ctx.ro = new ResizeObserver(() => this._resize())
        ctx.ro.observe(holder)
        ctx.onResize = () => this._resize()
        window.addEventListener('resize', ctx.onResize)

        this._animate(el)
        this.ready = true
      },

      _addRepresentations(ctx, root) {
        const { THREE, scene } = ctx
        root.traverse((child) => {
          if (child.isMesh) {
            ctx.solid.push(child)
            // wire + points copy the solid mesh's local transform (GLB nodes carry non-identity
            // transforms), else the alt modes render offset and the view jumps on switch.
            const wire = new THREE.Mesh(child.geometry, new THREE.MeshBasicMaterial(
              { color: 0x111111, wireframe: true, transparent: true, opacity: 0.55 }))
            wire.position.copy(child.position)
            wire.quaternion.copy(child.quaternion)
            wire.scale.copy(child.scale)
            wire.visible = false
            ctx.wire.push(wire)
            child.parent.add(wire)

            const pts = new THREE.Points(child.geometry, new THREE.PointsMaterial(
              { color: 0x111111, size: 0.0016 }))
            pts.position.copy(child.position)
            pts.quaternion.copy(child.quaternion)
            pts.scale.copy(child.scale)
            pts.visible = false
            ctx.points.push(pts)
            child.parent.add(pts)
          }
        })
        ctx.root = root
        scene.add(root)
      },

      // Frame to the bounding sphere (angle-invariant → centered at any rotation).
      _frameModel(ctx) {
        const { THREE, camera, controls, root } = ctx
        root.updateWorldMatrix(true, true)
        const box = new THREE.Box3().setFromObject(root)
        const sphere = box.getBoundingSphere(new THREE.Sphere())
        const fov = (camera.fov * Math.PI) / 180
        const dist = (sphere.radius / Math.sin(fov / 2)) * 1.05
        controls.target.copy(sphere.center)
        const cd = (this.$root.dataset.camDir || '0,-0.75,0.66').split(',').map(Number)
        const dir = new THREE.Vector3(cd[0] || 0, cd[1] || 0, cd[2] || 1).normalize()
        camera.position.copy(sphere.center).addScaledVector(dir, dist)
        camera.near = dist / 100
        camera.far = dist * 100
        camera.updateProjectionMatrix()
        controls.update()
      },

      _applyThreeMode(m) {
        const ctx = scenes.get(this.$root)
        if (!ctx) return
        ctx.solid.forEach((o) => { o.visible = m === 'solid' })
        ctx.wire.forEach((o) => { o.visible = m === 'wireframe' })
        ctx.points.forEach((o) => { o.visible = m === 'points' })
      },

      _resize() {
        const ctx = scenes.get(this.$root)
        if (!ctx) return
        const { renderer, camera, holder } = ctx
        const r = holder.getBoundingClientRect()
        const w = r.width || 600
        const h = r.height || 600
        renderer.setSize(w, h)
        camera.aspect = w / h
        camera.updateProjectionMatrix()
      },

      _animate(el) {
        const ctx = scenes.get(el)
        if (!ctx) return
        ctx.raf = requestAnimationFrame(() => this._animate(el))
        ctx.controls.update()
        ctx.renderer.render(ctx.scene, ctx.camera)
      },
    }
  },
}
